/**
 * Slot-assignment editor for a combo's `combo_dishes`.
 * Operator picks a slot from the closed-set enum (`main` / `main2` /
 * `main3` / `side` / `soup` / `staple`) defined in the backend's
 * `COMBO_SLOTS` (#80) and mirrored on the frontend via
 * `@/services/catalog-enums` (#83 method B). The cross-repo sync
 * test enforces backend ↔ frontend parity at CI time.
 *
 * Operator selects a dish from the catalog, sets display_order, and
 * saves the whole batch via `PUT /catalog/combos/:id/dishes`. Backend
 * pre-validates atomically (issue #19) — duplicate (slot, dish_id),
 * missing dish, archived dish all surface via the discriminated
 * reasons in the ADR-0006 envelope's `error.extras.reasons` (HTTP
 * 422). Off-enum slot values fail at the route boundary (issue #80).
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Loading,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { comboAPI, dishAPI, type Dish, type Combo } from '@/services';
import { COMBO_SLOTS, type ComboSlot } from '@/services/catalog-enums';
import { ApiError } from '@lib/api';
import { resolveApiError } from '@lib/errors';

interface Props {
  combo: Combo;
}

interface SlotRow {
  /** See RecipeEditorCard.RecipeRow.clientId — same rationale. */
  clientId: string;
  /** Closed-set slot per backend `combo_dishes.slot` column
   *  (`ComboSlot` from `@/services/catalog-enums`). Empty string
   *  represents "not yet picked"; the `<select>` only emits values
   *  in `COMBO_SLOTS` so unreachable strings can't land here at
   *  runtime, and the union type pins it at compile time. */
  slot: ComboSlot | '';
  dish_id: string;
  display_order: number;
}

let rowSeq = 0;
const nextClientId = () => `s${++rowSeq}`;

const newRow = (display_order: number): SlotRow => ({
  clientId: nextClientId(),
  slot: '',
  dish_id: '',
  display_order,
});

// Mirrors backend `routes/catalog/dishes.ts` listQuerySchema cap +
// `DishService.findMany` LIST_HARD_CAP.
const CATALOG_LIST_CAP = 100;

/**
 * Discriminated reason union from backend `setDishes` →
 * `error.extras.reasons` (see
 * `backend/src/contexts/kitchen/app/catalog.ts` SlotReason). Mirrored
 * here so the editor renders per-slot detail without cross-package
 * type coupling. Drift guard via exhaustive `_exhaustive: never` line
 * in formatInvalidSlots.
 */
type InvalidSlotReason =
  | { dish_id: string; reason: 'dish_missing' }
  | { dish_id: string; reason: 'dish_inactive' }
  | { slot: string; dish_id: string; reason: 'duplicate' };

function formatInvalidSlots(
  reasons: ReadonlyArray<InvalidSlotReason>,
  dishById: Map<string, Dish>,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const labelFor = (id: string) => dishById.get(id)?.name ?? id;
  return reasons
    .map((r) => {
      switch (r.reason) {
        case 'dish_missing':
          return t('combo.slots.error.line.dishMissing', { dish: labelFor(r.dish_id) });
        case 'dish_inactive':
          return t('combo.slots.error.line.dishInactive', { dish: labelFor(r.dish_id) });
        case 'duplicate':
          return t('combo.slots.error.line.duplicate', { slot: r.slot, dish: labelFor(r.dish_id) });
        default: {
          const _exhaustive: never = r;
          return _exhaustive;
        }
      }
    })
    .join('\n');
}

export function SlotEditorCard({ combo }: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const slotsQuery = useQuery({
    queryKey: ['catalog-combos', 'slots', combo.id],
    queryFn: () => comboAPI.slots(combo.id),
  });
  // Filter to active dishes — backend `setDishes` rejects inactive
  // dishes via `dish_inactive`. Keeping them out of the dropdown
  // pre-empts a 400 the operator could not have understood was
  // coming. Matches the backend's pre-validate contract (#19).
  const dishesQuery = useQuery({
    queryKey: ['catalog-dishes', 'list-active'],
    queryFn: () => dishAPI.list({ limit: CATALOG_LIST_CAP, extraParams: { status: 'active' } }),
  });

  // Detect dish-catalog overflow (same rationale as
  // RecipeEditorCard: surface the silent truncation loud).
  const overflowWarnedRef = useRef(false);
  const dishTotal = dishesQuery.data?.total;
  useEffect(() => {
    if (
      typeof dishTotal === 'number' &&
      dishTotal > CATALOG_LIST_CAP &&
      !overflowWarnedRef.current
    ) {
      overflowWarnedRef.current = true;
      toast.error(
        t('combo.slots.catalogTruncatedTitle'),
        t('combo.slots.catalogTruncatedDetail', { shown: CATALOG_LIST_CAP, total: dishTotal }),
      );
    }
  }, [dishTotal, toast, t]);

  const dishById = useMemo(() => {
    const map = new Map<string, Dish>();
    for (const d of dishesQuery.data?.items ?? []) {
      map.set(d.id, d);
    }
    return map;
  }, [dishesQuery.data]);

  const [rows, setRows] = useState<SlotRow[]>([]);
  // Hydrate rows when we first see data for THIS combo.id. See
  // RecipeEditorCard hydratedForRef for the same rationale —
  // navigation between combos without unmount must re-hydrate.
  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (hydratedForRef.current === combo.id) return;
    if (slotsQuery.data?.slots) {
      hydratedForRef.current = combo.id;
      setRows(
        slotsQuery.data.slots.map((s) => ({
          clientId: nextClientId(),
          slot: s.slot,
          dish_id: s.dish_id,
          display_order: s.display_order,
        })),
      );
    }
  }, [combo.id, slotsQuery.data]);

  /**
   * Build the slot payload from local rows. See
   * RecipeEditorCard.buildSavePayload for the rationale (no silent
   * drop of partially-filled rows). `slot` is `ComboSlot` here —
   * the `!r.slot` filter below narrows from the row-level
   * `ComboSlot | ''` to the API-level `ComboSlot`.
   */
  type SlotPayload = { slot: ComboSlot; dish_id: string; display_order: number };

  function buildSavePayload(): { slots: SlotPayload[]; skipped: number } {
    let skipped = 0;
    const slots: SlotPayload[] = [];
    for (const r of rows) {
      const isFullyEmpty = !r.slot && !r.dish_id && r.display_order === 0;
      if (isFullyEmpty) continue;
      if (!r.slot || !r.dish_id) {
        skipped += 1;
        continue;
      }
      // After `!r.slot` filter, `r.slot` cannot be `''`.
      const slot: ComboSlot = r.slot;
      slots.push({ slot, dish_id: r.dish_id, display_order: r.display_order });
    }
    return { slots, skipped };
  }

  const saveMutation = useMutation({
    mutationFn: (slots: SlotPayload[]) => comboAPI.setSlots(combo.id, slots),
    onSuccess: () => {
      hydratedForRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['catalog-combos', 'slots', combo.id] });
      toast.success(t('combo.slots.saved'));
    },
    onError: (err) => {
      // Atomic pre-validate (issue #19) returns the ADR-0006 envelope
      // `{ error: { extras: { reasons: [...] } } }` at HTTP 422 —
      // `lib/api.ts` surfaces `error.extras` on `ApiError.extras`, so
      // read the reasons off `err.extras.reasons` (NOT the archived
      // backend's top-level `extras.invalid_slots`). Surface them
      // line-by-line — `resolveApiError` only handles the
      // string-sentinel path which this 422 does not emit.
      const invalidSlots =
        err instanceof ApiError
          ? (err.extras as { reasons?: unknown } | undefined)?.reasons
          : undefined;
      if (Array.isArray(invalidSlots) && invalidSlots.length > 0) {
        const detail = formatInvalidSlots(invalidSlots as InvalidSlotReason[], dishById, t);
        toast.error(t('combo.slots.saveFailed'), detail);
        return;
      }
      toast.error(
        t('combo.slots.saveFailed'),
        resolveApiError(err, i18n, { namespace: 'combo', fallbackKey: 'combo.slots.saveFailed' }),
      );
    },
  });

  function update(index: number, patch: Partial<Omit<SlotRow, 'clientId'>>) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  function addRow() {
    // Compute next display_order against `prev` inside setRows so
    // two rapid clicks of "Add slot" do not collide on the same
    // pre-update snapshot — both invocations would otherwise
    // produce the same `Math.max(...) + 1` and the second row
    // ships with a duplicate display_order.
    setRows((prev) => {
      const nextOrder = prev.length > 0 ? Math.max(...prev.map((r) => r.display_order)) + 1 : 0;
      return [...prev, newRow(nextOrder)];
    });
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  // Re-entrancy guard for double-click — see
  // RecipeEditorCard.handleSaveClick for the rationale.
  const saveInFlightRef = useRef(false);
  function handleSaveClick() {
    if (saveInFlightRef.current || saveMutation.isPending) return;
    const { slots, skipped } = buildSavePayload();
    if (skipped > 0) {
      toast.warning(
        t('combo.slots.savedWithSkippedTitle'),
        t('combo.slots.savedWithSkippedDetail', { count: skipped }),
      );
    }
    saveInFlightRef.current = true;
    saveMutation.mutate(slots, {
      onSettled: () => {
        saveInFlightRef.current = false;
      },
    });
  }

  const initialLoadFailed =
    (slotsQuery.isError && !slotsQuery.data) || (dishesQuery.isError && !dishesQuery.data);
  if (initialLoadFailed) {
    const err = slotsQuery.error ?? dishesQuery.error;
    return (
      <Card data-testid="slot-editor-card">
        <CardHeader>
          <CardTitle>{t('combo.slots.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive" role="alert">
            {resolveApiError(err, i18n, {
              namespace: 'combo',
              fallbackKey: 'combo.slots.loadFailed',
            })}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (slotsQuery.isLoading || dishesQuery.isLoading) {
    return <Loading />;
  }

  const refetchFailed = slotsQuery.isError || dishesQuery.isError;

  return (
    <Card data-testid="slot-editor-card">
      <CardHeader>
        <CardTitle>{t('combo.slots.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {refetchFailed && (
          <p className="text-xs text-destructive" role="alert" data-testid="slot-refetch-error">
            {resolveApiError(slotsQuery.error ?? dishesQuery.error, i18n, {
              namespace: 'combo',
              fallbackKey: 'combo.slots.loadFailed',
            })}
          </p>
        )}
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('combo.slots.empty')}</p>
        ) : (
          rows.map((row, i) => (
            <div
              key={row.clientId}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end"
              data-testid={`slot-row-${i}`}
            >
              <div className="md:col-span-3">
                <label className="text-xs text-muted-foreground">{t('combo.slots.slot')}</label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                  value={row.slot}
                  onChange={(e) => update(i, { slot: e.target.value as ComboSlot | '' })}
                  data-testid={`slot-select-${i}`}
                >
                  <option value="">—</option>
                  {COMBO_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>
                      {t(`combo.slots.enum.${slot}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-5">
                <label className="text-xs text-muted-foreground">{t('combo.slots.dish')}</label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                  value={row.dish_id}
                  onChange={(e) => update(i, { dish_id: e.target.value })}
                >
                  <option value="">—</option>
                  {(dishesQuery.data?.items ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">
                  {t('combo.slots.displayOrder')}
                </label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={row.display_order}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    update(i, { display_order: Number.isNaN(parsed) ? 0 : Math.max(0, parsed) });
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(i)}
                  aria-label={t('ui.remove')}
                >
                  ✕
                </Button>
              </div>
            </div>
          ))
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={addRow}>
            + {t('combo.slots.addRow')}
          </Button>
          <Button size="sm" onClick={handleSaveClick} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t('ui.saving') : t('combo.slots.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
